// Copyright 2026, Imprivata, Inc.  All rights reserved.

// New flags: add an entry with featureFlagKey/featureFlagType/default; the flag must exist in
// astra-feature-flags for every environment. Don't add legacy flags. To migrate legacy → server:
// verify per-env parity in astra-feature-flags, add the entry here, then remove the key
// from flags.json and FeatureConfig in `featureFlagsUtils`.
//
// Render-timing (server flags): values are populated only after Session primes the cache.
// Components rendered after the Session gate call featureSupported() directly; components
// rendered before or concurrent with Session (e.g. PortalRoutes) must use useFeatureFlagValue().

export type FeatureFlagType = 'boolean' | 'string' | 'integer' | 'double';
export type FlagValue = boolean | string | number;

type LegacyFlag = { legacy: true };
export type ServerFlagEntry = {
  featureFlagKey: string;
  featureFlagType: FeatureFlagType;
  default: FlagValue;
};

export type FlagEntry = LegacyFlag | ServerFlagEntry;

export const FEATURE_FLAG_REGISTRY = {
  // --- Legacy flags (source: featureFlags.json ladder walk). Migrate one at a time. ---
  PENDO_API_KEY:                     { legacy: true },
  ENABLE_EPCS:                       { legacy: true },
  ENABLE_LOGIN_ACTIVITY_REPORT:      { legacy: true },
  ENABLE_ENROLLMENT_ACTIVITY_REPORT: { legacy: true },
  PASSWORD_EXPIRATION_ENABLED:       { legacy: true },
  ENABLE_TEST_ENTRA_ID:              { legacy: true },
  ENABLE_EMAIL_OTP:                  { legacy: true },
  ENABLE_ENDPOINTS:                  { legacy: true },
  ENABLE_CUSTOM_IDV_TEMPLATE:        { legacy: true },

  // --- Server flags (BFF feature-flags endpoint) ---
  ENABLE_EAMC_CHATBOT: {
    featureFlagKey: 'eamc-chatbot.enabled',
    featureFlagType: 'boolean',
    default: false,
  },
  EAMC_APG_SSO_ENABLED: {
    featureFlagKey: 'eamc.apg-sso-enabled',
    featureFlagType: 'boolean',
    default: false,
  },
  RISK_AWARENESS_ENABLED: {
    featureFlagKey: 'amportalui.risk-awareness-enabled',
    featureFlagType: 'boolean',
    default: false,
  },
} as const satisfies Record<string, FlagEntry>;

export type FeatureFlagKey = keyof typeof FEATURE_FLAG_REGISTRY;
